import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCaptcha, apiLogin, setToken, getToken } from '../api.js';

export default function Login() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captcha, setCaptcha] = useState({ svg: '', token: '' });
  const [needCaptcha, setNeedCaptcha] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) navigate('/admin', { replace: true });
  }, [navigate]);

  const refreshCaptcha = async () => {
    try {
      const data = await apiCaptcha();
      setCaptcha({ svg: data.svg, token: data.token });
      setCaptchaAnswer('');
    } catch {
      /* 静默 */
    }
  };

  useEffect(() => {
    if (needCaptcha) refreshCaptcha();
  }, [needCaptcha]);

  const mapError = (err) => {
    if (err?.code === 'NETWORK') return '登录失败：网络错误，无法连接到服务器';
    if (err?.status >= 500) return '登录失败：服务器错误';
    if (err?.status === 401) {
      const msg = err?.data?.error || '';
      if (/verification|captcha|验证/i.test(msg)) return '验证码错误，请重新输入';
      return '登录失败：用户名或密码错误';
    }
    return '登录失败：未知错误';
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await apiLogin({
        username,
        password,
        captchaAnswer: captchaAnswer.trim().toLowerCase(),
        captchaToken: captcha.token
      });
      if (data?.token) {
        setToken(data.token);
        navigate('/admin', { replace: true });
      } else {
        setError('登录失败：未知错误');
      }
    } catch (err) {
      setError(mapError(err));
      // 失败后需要验证码（与后端 requireCaptcha 语义一致）
      if (err?.data?.requireCaptcha || err?.status === 401) setNeedCaptcha(true);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-xl shadow-md overflow-hidden p-8">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">管理后台登录</h2>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">用户名</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} required className={inputCls} type="text" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">密码</label>
            <input value={password} onChange={(e) => setPassword(e.target.value)} required className={inputCls} type="password" />
          </div>

          {needCaptcha && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">验证码</label>
              <div className="flex items-center gap-3">
                <div className="h-10 flex-shrink-0 rounded-lg border border-gray-300 bg-gray-50 px-2 flex items-center" dangerouslySetInnerHTML={{ __html: captcha.svg }} />
                <input value={captchaAnswer} onChange={(e) => setCaptchaAnswer(e.target.value)} placeholder="请输入上方字符" className={inputCls} />
                <button type="button" onClick={refreshCaptcha} className="flex-shrink-0 whitespace-nowrap text-xs text-blue-600 hover:underline">刷新验证码</button>
              </div>
            </div>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}

          <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 rounded-lg transition-colors disabled:opacity-60">
            {loading ? '登录中…' : '登录'}
          </button>
        </form>
      </div>
    </div>
  );
}
